import React, { useMemo } from 'react';
import { Sparkles, Flag, Users, Mail } from 'lucide-react';

// Live counts of inbound leads by program category, derived from the
// contact_submissions list already fetched by AdminPage. No new endpoint —
// this stays in sync automatically the next time contacts refresh.
//
// Category detection matches the subjects the frontend forms compose:
//   • Girls Flag  → "Girls Flag Football — Express Interest"
//   • Jamboree    → "Jamboree Interest — ..."
//   • Other       → everything else

const CARD_STYLE = {
  background: 'linear-gradient(135deg, #111 0%, #1a1a1a 60%, #0a0a0a 100%)',
  border: '1px solid rgba(255, 215, 0, 0.25)',
  borderRadius: '14px',
  padding: '1rem 1.15rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.35rem',
  minWidth: '150px',
  flex: '1 1 180px',
};

const HIGHLIGHT_CARD_STYLE = {
  ...CARD_STYLE,
  border: '2px solid #FFD700',
  boxShadow: '0 8px 24px rgba(255, 215, 0, 0.2)',
};

const LABEL_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
  color: '#FFD700',
  fontSize: '0.68rem',
  fontWeight: 800,
  letterSpacing: '2px',
  textTransform: 'uppercase',
};

const VALUE_STYLE = {
  color: '#fff',
  fontSize: '2rem',
  fontWeight: 900,
  lineHeight: 1,
};

const SUB_STYLE = {
  color: '#a3a3a3',
  fontSize: '0.72rem',
  fontWeight: 500,
};

const WRAPPER_STYLE = {
  display: 'flex',
  gap: '0.85rem',
  flexWrap: 'wrap',
  margin: '0 0 1.25rem 0',
};

const InterestLeaderboard = ({ contacts = [] }) => {
  const stats = useMemo(() => {
    let girlsFlag = 0;
    let jamboree = 0;
    let other = 0;
    let last24h = 0;
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;

    for (const c of contacts) {
      const subj = (c.subject || '').toLowerCase();
      if (subj.includes('girls flag')) girlsFlag += 1;
      else if (subj.includes('jamboree')) jamboree += 1;
      else other += 1;

      const created = c.created_at ? new Date(c.created_at).getTime() : 0;
      if (created && created >= cutoff) last24h += 1;
    }
    return { girlsFlag, jamboree, other, last24h, total: contacts.length };
  }, [contacts]);

  return (
    <div style={WRAPPER_STYLE} data-testid="interest-leaderboard">
      <div style={HIGHLIGHT_CARD_STYLE} data-testid="lb-girls-flag">
        <span style={LABEL_STYLE}>
          <Sparkles size={13} />
          Girls Flag
        </span>
        <span style={VALUE_STYLE}>{stats.girlsFlag}</span>
        <span style={SUB_STYLE}>Express Interest submissions</span>
      </div>

      <div style={CARD_STYLE} data-testid="lb-jamboree">
        <span style={LABEL_STYLE}>
          <Flag size={13} />
          Jamboree
        </span>
        <span style={VALUE_STYLE}>{stats.jamboree}</span>
        <span style={SUB_STYLE}>Panthers Classic leads</span>
      </div>

      <div style={CARD_STYLE} data-testid="lb-other">
        <span style={LABEL_STYLE}>
          <Mail size={13} />
          Other
        </span>
        <span style={VALUE_STYLE}>{stats.other}</span>
        <span style={SUB_STYLE}>General contact messages</span>
      </div>

      <div style={CARD_STYLE} data-testid="lb-24h">
        <span style={LABEL_STYLE}>
          <Users size={13} />
          Last 24h
        </span>
        <span style={VALUE_STYLE}>{stats.last24h}</span>
        <span style={SUB_STYLE}>New across all categories</span>
      </div>
    </div>
  );
};

export default InterestLeaderboard;
